import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';

/**
 * PurchaseModal
 *
 * Shows an item for sale, how many the user already owns, lets them pick
 * a quantity, then kicks off a Stripe Checkout session.
 *
 * attrs:
 *   priceId      {string}  Stripe Price ID
 *   itemKey      {string}  Your internal key, e.g. "donation_5"
 *   itemName     {string}  Display name, e.g. "Gold Badge"
 *   priceLabel   {string}  e.g. "$5.00"
 *   description  {string}  Short blurb
 *   maxQty       {number}  Max selectable quantity (default 10)
 *   ownedCount   {number}  How many the user already has (from /api/stripe/me)
 */
export default class PurchaseModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.quantity = Stream(1);
    this.loading = false;
  }

  className() {
    return 'StripePurchaseModal Modal--small';
  }

  title() {
    return this.attrs.itemName || 'Purchase';
  }

  content() {
    const { priceLabel, description, ownedCount, maxQty = 10 } = this.attrs;
    const qty = this.quantity();

    return (
      <div className="Modal-body">

        {/* How many already owned */}
        {ownedCount > 0 && (
          <div className="Alert Alert--success" style="margin-bottom:16px">
            <span className="fas fa-box-open" style="margin-right:6px" />
            You own <strong>{ownedCount}</strong> of this item.
          </div>
        )}

        {description && <p style="margin-bottom:16px">{description}</p>}

        <div className="Form">

          {/* Price per unit */}
          <div className="Form-group">
            <label>Price</label>
            <div className="helpText">{priceLabel} per item</div>
          </div>

          {/* Quantity selector */}
          <div className="Form-group">
            <label>Quantity</label>
            <div style="display:flex;align-items:center;gap:12px;margin-top:4px">
              <Button
                className="Button"
                disabled={qty <= 1}
                onclick={() => { this.quantity(Math.max(1, qty - 1)); m.redraw(); }}
              >
                −
              </Button>
              <span style="font-size:1.2em;min-width:24px;text-align:center">{qty}</span>
              <Button
                className="Button"
                disabled={qty >= maxQty}
                onclick={() => { this.quantity(Math.min(maxQty, qty + 1)); m.redraw(); }}
              >
                +
              </Button>
              <span className="helpText" style="margin:0">
                {qty > 1 ? `= ${qty} × ${priceLabel}` : ''}
              </span>
            </div>
          </div>

          {/* Test mode notice */}
          {app.forum.attribute('kemze-stripe.testMode') && (
            <div className="Alert" style="margin-bottom:12px;font-size:0.85em">
              <strong>Test mode active.</strong> Use card <code>4242 4242 4242 4242</code>,
              any future expiry, any CVC.
            </div>
          )}

          <div className="Form-group" style="margin-top:20px">
            <Button
              className="Button Button--primary Button--block"
              loading={this.loading}
              disabled={this.loading}
              onclick={this.submit.bind(this)}
            >
              {this.loading ? 'Redirecting…' : `Buy ${qty > 1 ? `${qty} ×` : ''} ${this.attrs.itemName}`}
            </Button>
          </div>

        </div>
      </div>
    );
  }

  submit() {
    this.loading = true;
    m.redraw();

    app.request({
      method: 'POST',
      url: `${app.forum.attribute('apiUrl')}/stripe/checkout`,
      body: {
        type: 'one_time',
        price_id: this.attrs.priceId,
        item_key: this.attrs.itemKey,
        quantity: this.quantity(),
      },
    })
      .then(result => {
        window.location.href = result.url;
      })
      .catch(() => {
        this.loading = false;
        m.redraw();
        app.alerts.show({ type: 'error' }, 'Payment failed to start. Please try again.');
      });
  }
}
